import { PortalTelemetryEvent } from '../../../app/telemetry/portalTelemetryEvents';
import { validationIssues } from '../../../app/telemetry/validationTelemetry';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CpdFormMode, cpdFormConfig } from './cpdFormConfig';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory, useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useAppDispatch, useAppSelector } from '../../../app/store/configureStore';
import { addCpdDetailsAsync, cloneCpdDetailsAsync, updateCpdDetailsAsync, getCpdDetailsAsync } from '../../../app/slices/creditsSlice';
import { useReferenceData } from '../../../app/customHooks/useReferenceData';
import { useGetPcsEnrolmentYears } from '../../../app/customHooks/useGetPcsEnrolmentYears';
import { UserClaims } from '../../../app/enums/userClaims';
import { ApiRequestStatus } from '../../../app/enums/apiRequestStatus';
import { AppPathEnum } from '../../../app/enums/appPathEnum';
import { API_REQUEST_FULLFILLED, MESSAGE_DETAILS_SAVED } from '../../../app/utils/constant';
import { AutocompleteItem } from '../../../app/models/common/autocompleteItem';
import { MapActivityTypesToAutocompleteItems, MapCategoriesToAutocompleteItems, MapDomainsToCheckboxItems } from '../../../app/utils/convertStaticDataToDropdownItems';
import { portalScreenContext, trackPortalEvent } from '../../../app/telemetry/portalTelemetry';
import { usePortalScreen } from '../../../app/telemetry/usePortalTelemetry';
import { CpdDto } from '../models/cpdDto';
import { cpdValidationSchema } from './cpdValidationSchema';
import { cpdDefaultValues, getCpdActivityYear, mapCpdToFormValues, mapCpdFormToDto } from './cpdFormHelpers';

export function useCpdForm(mode: CpdFormMode) {
    const { control, setValue, watch, reset, handleSubmit, getFieldState } = useForm({
        mode: 'all', resolver: yupResolver(cpdValidationSchema), defaultValues: cpdDefaultValues
    });

    const dispatch = useAppDispatch();
    const history = useHistory();
    const { creditRecordId } = useParams<{ creditRecordId: string }>();
    const { accounts } = useMsal();
    const personId = accounts[0]?.idTokenClaims?.[UserClaims.EXTENSION_PORTALCRM_PERSONID] as string ?? '';
    const { cpdCategories, activityTypes, goodPracticeDomains, verificationStatuses, staticDataStateStatus } = useAppSelector(state => state.ReferenceData);
    const { cpdDetails, getCpdStatus, addCpdStateStatus, updateCpdStateStatus, cloneCpdStateStatus } = useAppSelector(state => state.credits);
    const { enrolmentYears, enrolmentYearsStatus } = useAppSelector(state => state.pcsEnrolment);

    const [filteredActivityTypes, setFilteredActivityTypes] = useState<AutocompleteItem[]>([]);
    const [canShowDialog, setCanShowDialog] = useState(false);
    const [canShowNoEnrolmentDialog, setCanShowNoEnrolmentDialog] = useState(false);
    const [dialogDescription, setDialogDescription] = useState('');

    useReferenceData();
    useGetPcsEnrolmentYears();

    useEffect(() => {
        const subscription = watch((values, { name, type }) => {
            if (type !== 'change') return;
            if (name === 'Category') {
                const visibleTypes = activityTypes?.filter(x => x.cpdCategoryId === values.Category && x.userVisible) ?? [];
                setFilteredActivityTypes(MapActivityTypesToAutocompleteItems(visibleTypes));
                return;
            }
            if (name !== 'ActivityDate' || !enrolmentYears || !verificationStatuses) return;
            const activityYear = getCpdActivityYear(values.ActivityDate);
            if (activityYear === null) return;
            const enrolment = enrolmentYears.find(x => x.year === activityYear);
            if (!enrolment) {
                setCanShowNoEnrolmentDialog(true);
                setDialogDescription('Credit upload is not allowed as you are not enrolled for this year.');
                return;
            }
            const status = verificationStatuses.find(x => x.id === enrolment.verificationStatusId);
            setCanShowDialog(!status?.userEditable);
            setDialogDescription('Updates to CPD records related to enrolment years that are undergoing verification process are not allowed.');
        });
        return () => subscription.unsubscribe();
    }, [watch, activityTypes, enrolmentYears, verificationStatuses]);

    useEffect(() => {
        if (mode === 'Add') return;
        dispatch(getCpdDetailsAsync(creditRecordId)).then(response => {
            if (mode === 'Clone' && response.meta.requestStatus === API_REQUEST_FULLFILLED) {
                reset(mapCpdToFormValues(response.payload as CpdDto, true));
            }
        });
    }, [mode, creditRecordId, dispatch, reset]);

    useEffect(() => {
        if (mode === 'Update' && cpdDetails) reset(mapCpdToFormValues(cpdDetails, false));
    }, [mode, cpdDetails, reset]);

    useEffect(() => {
        if (mode === 'Add' || !cpdDetails || !activityTypes) return;
        setFilteredActivityTypes(MapActivityTypesToAutocompleteItems(activityTypes.filter(x =>
            x.cpdCategoryId === cpdDetails.cpdCategoryId && x.userVisible)));
    }, [mode, cpdDetails, activityTypes]);

    useEffect(() => {
        if (mode !== 'Update' || !cpdDetails || !enrolmentYears || !verificationStatuses) return;
        const year = getCpdActivityYear(cpdDetails.activityDate);
        if (year === null) return;
        const statusId = enrolmentYears.find(x => x.year === year)?.verificationStatusId;
        setCanShowDialog(!verificationStatuses.find(x => x.id === statusId)?.userEditable);
    }, [mode, cpdDetails, enrolmentYears, verificationStatuses]);

    const saveStatus = mode === 'Add' ? addCpdStateStatus : mode === 'Clone' ? cloneCpdStateStatus : updateCpdStateStatus;
    const isLoading = saveStatus === ApiRequestStatus.Pending || staticDataStateStatus === ApiRequestStatus.Pending ||
        enrolmentYearsStatus === ApiRequestStatus.Pending || (mode !== 'Add' && getCpdStatus === ApiRequestStatus.Pending);
    const screen = portalScreenContext(cpdFormConfig[mode].screen);
    usePortalScreen(screen, undefined, !isLoading);

    const onSubmit = handleSubmit(async values => {
        const dto = mapCpdFormToDto(values, personId, mode === 'Update' ? creditRecordId : undefined);
        const save = mode === 'Add' ? addCpdDetailsAsync : mode === 'Clone' ? cloneCpdDetailsAsync : updateCpdDetailsAsync;
        const response = await dispatch(save(dto));
        if (response.meta.requestStatus === API_REQUEST_FULLFILLED) {
            const id = mode === 'Update' ? creditRecordId : response.payload;
            toast.success(MESSAGE_DETAILS_SAVED);
            history.push(AppPathEnum.PCS_VIEW_CPD + '/' + id + window.location.search);
        }
    }, () => trackPortalEvent(screen, PortalTelemetryEvent.ValidationBlocked, { step: undefined, validationErrors: validationIssues(cpdFormConfig[mode].screen, getFieldState) }));

    const onCloseDialog = () => {
        setCanShowDialog(false);
        setCanShowNoEnrolmentDialog(false);
        setValue('ActivityDate', undefined);
    };

    return {
        control, creditRecordId, filteredActivityTypes, isLoading, onSubmit, onCloseDialog,
        showRestrictionDialog: canShowDialog || canShowNoEnrolmentDialog, dialogDescription,
        categoryItems: MapCategoriesToAutocompleteItems(cpdCategories?.filter(x => x.userVisible) ?? []),
        domainOptions: MapDomainsToCheckboxItems(goodPracticeDomains ?? [])
    };
}
